import { useMemo } from 'react';
import type { DriverLocation } from '@breaker/shared-types';
import { LocationMap } from '@/components/location-map';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { useActiveFleet } from '@/data/fleets';
import { useFleetLatestLocations } from '@/data/fleet-locations';
import { useFleetMembers, indexMembers } from '@/data/members';
import { formatRelative, formatDateTime, shortId } from '@/lib/format';

/**
 * Fleet-wide live map. The LIST is roster-driven: every active
 * driver-role member appears, whether or not they have ever shared a
 * position — a driver with sharing off gets an explicit "No location
 * yet" row, never silent omission (the invite-rail driver bug: the
 * latest-locations endpoint reads driver_locations only, so a driver
 * with zero rows simply didn't exist here). The MAP renders positions
 * only. Non-driver sharers with positions (e.g. an owner who shared)
 * keep their rows. Polls every 15s.
 */

/** One row: an active driver, a sharer, or both. */
interface MapRow {
  userId: string;
  point: DriverLocation | null;
}

/** One timestamp, formatted once: relative while fresh, absolute after. */
function lastSeenLabel(iso: string): string {
  const age = Date.now() - new Date(iso).getTime();
  return age < 86_400_000 ? formatRelative(iso) : formatDateTime(iso);
}

export function FleetMapPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;

  const locationsQuery = useFleetLatestLocations(fleetId);
  const membersQuery = useFleetMembers(fleetId);

  const memberById = useMemo(
    () => indexMembers(membersQuery.data),
    [membersQuery.data],
  );

  const points = locationsQuery.data ?? [];

  // Newest-first for the list; the map handles its own framing.
  const sorted = useMemo(
    () =>
      [...points].sort(
        (a, b) =>
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
      ),
    [points],
  );

  // Sharers (newest first), then every active driver who has no position
  // yet (alphabetical — useFleetMembers order is not guaranteed).
  const rows = useMemo<MapRow[]>(() => {
    const sharerIds = new Set(sorted.map((p) => p.userId));
    const quiet = (membersQuery.data ?? [])
      .filter(
        (m) =>
          m.role === 'driver' &&
          m.status === 'active' &&
          !sharerIds.has(m.userId),
      )
      .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));
    return [
      ...sorted.map((p) => ({ userId: p.userId, point: p })),
      ...quiet.map((m) => ({ userId: m.userId, point: null })),
    ];
  }, [sorted, membersQuery.data]);

  // userId → displayName for the map popups, resolved the same way the list is
  // (members displayName, shortId fallback for non-member sharers).
  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of sorted) {
      const member = memberById.get(p.userId);
      m.set(p.userId, member ? member.user.displayName : shortId(p.userId));
    }
    return m;
  }, [sorted, memberById]);

  if (!fleetId) {
    return (
      <div className="p-6">
        <p className="text-sm text-breaker-mute italic">
          Select a fleet to see its live map.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold text-breaker-ink">Live map</h1>
        <span className="text-2xs text-breaker-mute uppercase tracking-wider">
          {locationsQuery.isFetching ? 'Updating…' : 'Live · refreshes every 15s'}
        </span>
      </div>

      {locationsQuery.isLoading || membersQuery.isLoading ? (
        <div className="py-16 flex justify-center">
          <Spinner size="lg" />
        </div>
      ) : locationsQuery.isError ? (
        <EmptyState
          title="Couldn't load the fleet map"
          description={
            (locationsQuery.error as Error)?.message ??
            'The server returned an error.'
          }
          action={
            <Button
              onClick={() => locationsQuery.refetch()}
              variant="secondary"
              size="sm"
            >
              Retry
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No drivers in this fleet yet"
          description="Invite a driver and they'll appear here — with a live position once they share on a job."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4 items-start">
          {sorted.length > 0 ? (
            <LocationMap
              points={sorted}
              mode="markers"
              names={names}
              className="rounded-lg overflow-hidden border border-breaker-line"
            />
          ) : (
            <div className="rounded-lg border border-breaker-line bg-breaker-ground-2 py-16 px-6 text-center">
              <p className="text-sm text-breaker-mute">
                No positions to map yet — drivers appear here when they share
                on a job.
              </p>
            </div>
          )}
          <Card title={`Drivers · ${rows.length}`}>
            <ul className="divide-y divide-breaker-line">
              {rows.map(({ userId, point }) => {
                const member = memberById.get(userId);
                return (
                  <li key={userId} className="py-2 first:pt-0 last:pb-0">
                    <p className="text-sm font-medium text-breaker-ink">
                      {member ? (
                        member.user.displayName
                      ) : (
                        <span className="ref">{shortId(userId)}</span>
                      )}
                    </p>
                    {point ? (
                      <p className="text-2xs text-breaker-mute">
                        Last seen {lastSeenLabel(point.recordedAt)}
                      </p>
                    ) : (
                      <p className="text-2xs text-breaker-mute italic">
                        No location yet — sharing off or not started
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
